import { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppStore } from '../../stores/appStore';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, ArrowLeft, Loader2, Star, Shield, Users } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../components/ui/toast';
import { api, getRoles } from '../../services/api';
import type { Role } from '../../types';

interface RoleOption {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: string[];
}

const SYSTEM_ROLES: RoleOption[] = [
  {
    id: 'user',
    name: 'User',
    description: 'Basic access — can view their own profile and authenticate via applications',
    isSystem: true,
    permissions: [],
  },
  {
    id: 'admin',
    name: 'Organization Admin',
    description: 'Can manage applications and users within this organization',
    isSystem: true,
    permissions: [],
  },
];

const createUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less')
    .optional(),
  selectedRoleIds: z.array(z.string()).min(1, 'Select at least one role'),
  primaryRoleId: z.string().min(1, 'Choose a primary role'),
  sendVerificationEmail: z.boolean().optional(),
});

export function CreateUser() {
  const { isSuperAdmin } = useAuth();
  const { error, clearError } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customRoles, setCustomRoles] = useState<RoleOption[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const stateOrgId = (location.state as any)?.orgId;

  const form = useForm<z.infer<typeof createUserSchema>>({
    resolver: zodResolver(createUserSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      name: '',
      selectedRoleIds: ['user'],
      primaryRoleId: 'user',
      sendVerificationEmail: true,
    },
  });

  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => { clearError(); }, [clearError]);

  useEffect(() => {
    if (!stateOrgId) return;
    setLoadingRoles(true);
    getRoles(stateOrgId)
      .then((res: any) => {
        const roles: Role[] = res.roles || [];
        setCustomRoles(roles.map(r => ({
          id: r.id,
          name: r.name,
          description: r.description,
          isSystem: false,
          permissions: r.permissions,
        })));
      })
      .catch(() => {})
      .finally(() => setLoadingRoles(false));
  }, [stateOrgId]);

  if (!isSuperAdmin()) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <ThemeToggleCompact />
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Only super administrators can create users.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!stateOrgId) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-4">
          <Link to="/dashboard/users">
            <Button variant="ghost" className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create User</h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Register a new user account</p>
              </div>
            </div>
            <ThemeToggleCompact />
          </div>
        </div>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Missing Organization Context</AlertTitle>
          <AlertDescription>
            Cannot create user without organization context. Please navigate from the user list.
          </AlertDescription>
        </Alert>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Go back to the user list, select an organization, then click Create User.
            </p>
            <Link to="/dashboard/users">
              <Button>Go to User List</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = async (data: z.infer<typeof createUserSchema>) => {
    setIsSubmitting(true);
    clearError();

    try {
      const systemRoleIds = SYSTEM_ROLES.map(r => r.id);
      const allRoles = [...SYSTEM_ROLES, ...customRoles];

      // `role` = primary role's value: system roles use their id, custom roles use their name
      const primaryRole = allRoles.find(r => r.id === data.primaryRoleId);
      const apiRole = primaryRole
        ? (primaryRole.isSystem ? primaryRole.id : primaryRole.name)
        : data.primaryRoleId;

      // Build `roles` array: primary role + selected custom roles
      const customRoleNames = data.selectedRoleIds
        .filter(rid => !systemRoleIds.includes(rid))
        .map(rid => customRoles.find(r => r.id === rid)?.name)
        .filter((n): n is string => Boolean(n));

      const allRoleNames = [apiRole, ...customRoleNames.filter(n => n !== apiRole)];

      await api.post(`api/v1/organizations/${stateOrgId}/users`, {
        json: {
          email: data.email,
          password: data.password,
          name: data.name,
          role: apiRole,
          roles: allRoleNames,
          send_verification_email: data.sendVerificationEmail,
        }
      }).json();

      const primaryName = primaryRole?.name ?? data.primaryRoleId;
      toast({
        title: 'User Created',
        description: `${data.email} created with ${data.selectedRoleIds.length} role(s). Primary: ${primaryName}.`,
      });

      navigate('/dashboard/users', { state: { orgId: stateOrgId } });
    } catch (err: any) {
      toast({
        title: 'Creation Failed',
        description: err.message || 'Failed to create user',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedRoleIds = form.watch('selectedRoleIds');
  const primaryRoleId = form.watch('primaryRoleId');
  const allRoles = [...SYSTEM_ROLES, ...customRoles];

  const toggleRole = (roleId: string) => {
    const current = form.getValues('selectedRoleIds');
    const next = current.includes(roleId)
      ? current.filter(id => id !== roleId)
      : [...current, roleId];

    form.setValue('selectedRoleIds', next, { shouldValidate: true });

    // If we just unchecked the primary, auto-pick the first remaining selected role
    if (!next.includes(form.getValues('primaryRoleId'))) {
      form.setValue('primaryRoleId', next[0] ?? '', { shouldValidate: true });
    }
  };

  const setPrimary = (roleId: string) => {
    form.setValue('primaryRoleId', roleId, { shouldValidate: true });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to="/dashboard/users">
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create User</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Register a new user account</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>User Details</CardTitle>
          <CardDescription>Configure the new user's account information</CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Creation Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input placeholder="user@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password *</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormDescription>
                      Minimum 8 characters with uppercase, lowercase, and number
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Unified Role Selection */}
              <FormItem>
                <FormLabel>Roles *</FormLabel>
                <FormDescription>
                  Select one or more roles. Mark one as <span className="font-medium">Primary</span> — this determines the user's main account type.
                </FormDescription>

                <div className="border rounded-lg overflow-hidden mt-2">
                  {/* System roles section */}
                  <div className="bg-gray-50 dark:bg-gray-900/50 px-3 py-1.5 border-b">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      <Shield className="h-3 w-3" />
                      System Roles
                    </div>
                  </div>
                  {SYSTEM_ROLES.map((role, idx) => (
                    <RoleRow
                      key={role.id}
                      role={role}
                      isSelected={selectedRoleIds.includes(role.id)}
                      isPrimary={primaryRoleId === role.id}
                      isLast={idx === SYSTEM_ROLES.length - 1 && customRoles.length === 0}
                      onToggle={() => toggleRole(role.id)}
                      onSetPrimary={() => setPrimary(role.id)}
                      disabled={selectedRoleIds.length === 1 && selectedRoleIds.includes(role.id)}
                    />
                  ))}

                  {/* Custom roles section */}
                  {loadingRoles ? (
                    <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 border-t">
                      Loading custom roles…
                    </div>
                  ) : customRoles.length > 0 ? (
                    <>
                      <div className="bg-gray-50 dark:bg-gray-900/50 px-3 py-1.5 border-t">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          <Users className="h-3 w-3" />
                          Custom Roles
                        </div>
                      </div>
                      {customRoles.map((role, idx) => (
                        <RoleRow
                          key={role.id}
                          role={role}
                          isSelected={selectedRoleIds.includes(role.id)}
                          isPrimary={primaryRoleId === role.id}
                          isLast={idx === customRoles.length - 1}
                          onToggle={() => toggleRole(role.id)}
                          onSetPrimary={() => setPrimary(role.id)}
                          disabled={selectedRoleIds.length === 1 && selectedRoleIds.includes(role.id)}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500 border-t italic">
                      No custom roles for this organization —{' '}
                      <Link to="/dashboard/roles" className="text-blue-600 dark:text-blue-400 underline not-italic">
                        create some
                      </Link>
                    </div>
                  )}
                </div>

                {/* Validation errors */}
                {form.formState.errors.selectedRoleIds && (
                  <p className="text-sm text-red-500 mt-1">{form.formState.errors.selectedRoleIds.message}</p>
                )}
                {form.formState.errors.primaryRoleId && (
                  <p className="text-sm text-red-500 mt-1">{form.formState.errors.primaryRoleId.message}</p>
                )}

                {/* Summary */}
                {selectedRoleIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedRoleIds.map(id => {
                      const role = allRoles.find(r => r.id === id);
                      return role ? (
                        <Badge
                          key={id}
                          variant={id === primaryRoleId ? 'default' : 'secondary'}
                          className="gap-1"
                        >
                          {id === primaryRoleId && <Star className="h-2.5 w-2.5 fill-current" />}
                          {role.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </FormItem>

              <FormField
                control={form.control}
                name="sendVerificationEmail"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Send verification email</FormLabel>
                      <FormDescription>
                        The user will receive a link to verify their email address.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {isSubmitting ? 'Creating…' : 'Create User'}
                </Button>
                <Link to="/dashboard/users">
                  <Button variant="outline" type="button">Cancel</Button>
                </Link>
              </div>

            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Role row component ──────────────────────────────────────────────────────

interface RoleRowProps {
  role: RoleOption;
  isSelected: boolean;
  isPrimary: boolean;
  isLast: boolean;
  disabled: boolean;
  onToggle: () => void;
  onSetPrimary: () => void;
}

function RoleRow({ role, isSelected, isPrimary, isLast, disabled, onToggle, onSetPrimary }: RoleRowProps) {
  return (
    <div
      className={[
        'flex items-start gap-3 px-3 py-3 transition-colors',
        !isLast && 'border-b',
        isSelected ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40',
      ].filter(Boolean).join(' ')}
    >
      {/* Checkbox */}
      <Checkbox
        id={`role-check-${role.id}`}
        checked={isSelected}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="mt-0.5 shrink-0"
      />

      {/* Role info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <label
            htmlFor={`role-check-${role.id}`}
            className="text-sm font-medium cursor-pointer leading-none"
          >
            {role.name}
          </label>
          {role.isSystem ? (
            <Badge variant="outline" className="text-xs py-0 px-1.5 border-gray-300 text-gray-500">
              system
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs py-0 px-1.5 border-blue-300 text-blue-600 dark:text-blue-400">
              custom
            </Badge>
          )}
        </div>
        {role.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{role.description}</p>
        )}
        {role.permissions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {role.permissions.slice(0, 4).map(p => (
              <Badge key={p} variant="secondary" className="text-xs px-1.5 py-0 font-normal">{p}</Badge>
            ))}
            {role.permissions.length > 4 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0 font-normal">
                +{role.permissions.length - 4} more
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Primary toggle */}
      <div className="shrink-0">
        {isSelected ? (
          isPrimary ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-0.5">
              <Star className="h-3 w-3 fill-current" />
              Primary
            </span>
          ) : (
            <button
              type="button"
              onClick={onSetPrimary}
              className="text-xs text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 border border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 rounded px-2 py-0.5 transition-colors"
            >
              Set Primary
            </button>
          )
        ) : (
          <span className="text-xs text-gray-300 dark:text-gray-600 px-2 py-0.5">—</span>
        )}
      </div>
    </div>
  );
}
